import { useEffect } from 'react';

import camelCase from 'lodash/camelCase';
import get from 'lodash/get';
import PropTypes from 'prop-types';

import { Box } from '@mui/material';

import { useTransl } from 'shared/lib/i18n';

import AsyncTableComponent from '../Table/AsyncTableContainer';

import ExpandableImageComponent from '../Image/ExpandableImageComponent';

import { formatDate, isAdmin, toSentenceCase } from '../../utils';
import { getRoleName } from '../Person/utils';
import { getExportTitle } from '../ViewExport/utils';
import { getVisitorRoleFilterOptions } from './utils';

import { TABLE_CONSTANTS, TABLE_ID } from '../Table/constants';

import * as buildingSelectors from '../Building/selectors';

import { DATE_RANGES_TYPES, TABLE_DATA_TYPES } from '../../staticData';
import StyledLink from '../StyledLink';
import CellPersonWithImage from '../Table/CellPersonWithImage';
import {
  buildingFilter,
  limitFilter,
  roleFilter,
  sourceFilter,
  unitFilter,
} from '../Table/filters';
import Tooltip from '../Tooltip';
import VisitorLogTableExport from './VisitorLogTableExport';

const VisitorLogTableComponent = ({
  items,
  onLoad,
  getItems,
  getUnits,
  listParams,
  listKey,
  buildings,
  units,
  isVisitorProfile,
  onExport,
  onGetChartData,
}) => {
  const { t } = useTransl();

  const columns = [
    {
      Header: t('title.visitor'),
      id: 'visitor',
      accessor: ({ id, visitorId, person }) => {
        const linkUrl = id && `/visitors/profile/${visitorId}`;
        return <CellPersonWithImage person={person} linkUrl={linkUrl} />;
      },
      headerClassName: 'sort',
      show: !isVisitorProfile,
    },
    {
      Header: t('title.role'),
      id: 'role',
      accessor: ({ role }) => toSentenceCase(getRoleName(role)),
      width: 80,
      sortable: false,
      show: !isVisitorProfile,
    },
    {
      Header: t('title.building'),
      id: 'building_name',
      accessor: ({ building, buildingName }) => {
        if (building) {
          return (
            <StyledLink to={`/building/${building}`}>{buildingName}</StyledLink>
          );
        }
        return '';
      },
      headerClassName: 'sort',
      show: !isVisitorProfile,
    },
    {
      Header: t('title.unit'),
      id: 'unit',
      accessor: ({ building, unit, unitName }) => {
        if (building && unit && unitName) {
          return (
            <StyledLink to={`/building/${building}/units/${unit}`}>
              {unitName}
            </StyledLink>
          );
        }
        return t('roles.buildingVisitor');
      },
      headerClassName: 'sort',
      show: !isVisitorProfile,
    },
    {
      Header: t('title.doors'),
      id: 'doors',
      headerClassName: 'sort',
      accessor: ({ building, doors }) => {
        if (building && doors?.length) {
          return doors?.map(({ id, name }) =>
            isAdmin() ? (
              <StyledLink key={id} to={`/building/${building}/doors/${id}`}>
                {name}
              </StyledLink>
            ) : (
              <span key={id}>{name}</span>
            ),
          );
        }
        return '';
      },
    },
    {
      Header: t('title.snapshot'),
      id: 'snapshot',
      accessor: () => '',
      show: false,
    },
    {
      Header: t('title.comments'),
      id: 'comments',
      accessor: ({ comments, snapshots }) => (
        <Box display="flex" alignItems="center">
          {snapshots?.length > 0 && (
            <Box mr="1rem">
              <ExpandableImageComponent
                src={snapshots[0]?.url}
                caption={t('title.cameraSnapshot')}
                height="3.2rem"
              />
            </Box>
          )}
          <Tooltip tooltip={comments}>{comments}</Tooltip>
        </Box>
      ),
      sortable: false,
      minWidth: 150,
    },
    {
      Header: t('title.source'),
      id: 'source',
      accessor: ({ source }) =>
        buildingSelectors.getDoorOpeningMethodName(source),
      headerClassName: 'sort',
    },
    {
      Header: isVisitorProfile ? t('visitor.enteredOn') : t('visitor.authOn'),
      id: 'created_at',
      accessor: ({ createdAt }) => formatDate(createdAt),
      headerClassName: 'sort',
    },
  ];

  const filters = isVisitorProfile
    ? []
    : [
        buildingFilter(listParams, buildings),
        roleFilter(listParams, getVisitorRoleFilterOptions()),
        limitFilter(listParams),
        sourceFilter(listParams),
      ];

  const selectedBuildingId = listParams?.building;

  useEffect(() => {
    selectedBuildingId && !isVisitorProfile && getUnits(selectedBuildingId);
  }, [selectedBuildingId]);

  if (selectedBuildingId && !isVisitorProfile) {
    filters.splice(1, 0, unitFilter(listParams, units));
  }
  const visitor = {};
  const visitorId = get(listParams, 'visitor');
  if (visitorId) {
    visitor.id = get(listParams, 'visitor');
    visitor.firstName = get(listParams, 'firstName');
    visitor.lastName = get(listParams, 'lastName');
  }

  const defaultParams = {
    ordering: '-created_at',
  };
  const tableDataType = TABLE_DATA_TYPES.visitorsLog;

  const title = isVisitorProfile ? t('visitor.profile') : t('visitor.log');
  const exportTitle = getExportTitle(title);
  const exportActions = (
    <VisitorLogTableExport
      title={exportTitle}
      getDataForExport={onExport}
      columns={columns}
    />
  );

  const chartMetadata = isVisitorProfile
    ? null
    : {
        chartId: camelCase(title),
        title: t('visitor.lastVisitorsLogs'),
        label: t('visitor.visitorsLogs'),
        onGetChartData,
      };

  return (
    <AsyncTableComponent
      tableId={TABLE_ID.visitorsLogTable}
      title={title}
      columns={columns}
      defaultSort={TABLE_CONSTANTS.created_at}
      defaultSortDesc
      manual
      isHideSearch={isVisitorProfile}
      filters={filters}
      showFiltersAreApplied={false}
      items={items}
      onLoad={onLoad}
      getItems={getItems}
      listParams={listParams}
      defaultParams={defaultParams}
      tableDataType={tableDataType}
      listKey={listKey}
      dateRangeType={DATE_RANGES_TYPES.created_at}
      datePicker={!isVisitorProfile}
      exportActions={exportActions}
      chartMetadata={chartMetadata}
    />
  );
};

VisitorLogTableComponent.defaultProps = {
  isVisitorProfile: false,
  units: {},
  getUnits: () => {},
  onExport: () => {},
  onGetChartData: PropTypes.func,
};

VisitorLogTableComponent.propTypes = {
  items: PropTypes.instanceOf(Object).isRequired,
  buildings: PropTypes.instanceOf(Object).isRequired,
  units: PropTypes.instanceOf(Object),
  onLoad: PropTypes.func.isRequired,
  getItems: PropTypes.func.isRequired,
  getUnits: PropTypes.func,
  listParams: PropTypes.instanceOf(Object).isRequired,
  listKey: PropTypes.string.isRequired,
  isVisitorProfile: PropTypes.bool,
  onExport: PropTypes.func,
  onGetChartData: PropTypes.func,
};

export default VisitorLogTableComponent;
